import React from "react";
import {
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    Stack,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import type { EvaluationDomain, WhenDto } from "../../../../../types/resolver";
import { defaultKeyOperandForEvaluationDomain } from "../utils/defaultKeyOperand";
import { getWhenRuleType } from "./getWhenRuleType";
import {
    RuleAll,
    RuleAny,
    RuleNot,
    RuleComparator,
    RuleIn,
    RuleStartsWith,
    RuleEndsWith,
    RuleContains,
    RuleQuantifier,
} from "./Rules";
import type { QuantifierKind } from "./Rules";

const RULE_TYPE_GROUPS: { group?: string; options: { value: string; label: string }[] }[] = [
    { options: [{ value: "true", label: "Always true" }] },
    {
        group: "Equals / Not",
        options: [
            { value: "eq", label: "Equals" },
            { value: "ne", label: "Not equals" },
            { value: "not", label: "Not" },
        ],
    },
    {
        group: "Logical",
        options: [
            { value: "all", label: "All of (AND)" },
            { value: "any", label: "Any of (OR)" },
        ],
    },
    {
        group: "String",
        options: [
            { value: "startsWith", label: "Starts with" },
            { value: "endsWith", label: "Ends with" },
            { value: "contains", label: "Contains" },
        ],
    },
    {
        group: "Numeric",
        options: [
            { value: "gt", label: "Greater than" },
            { value: "gte", label: "Greater or equal" },
            { value: "lt", label: "Less than" },
            { value: "lte", label: "Less or equal" },
        ],
    },
    {
        group: "List / Quantifier",
        options: [
            { value: "in", label: "Value in list" },
            { value: "anyMatch", label: "Any match" },
            { value: "noneMatch", label: "None match" },
            { value: "allMatch", label: "All match" },
        ],
    },
];

export interface WhenBuilderProps {
    value: WhenDto | null | undefined;
    onChange: (when: WhenDto | null) => void;
    /** When true, hide "Remove" (e.g. for root). */
    noRemove?: boolean;
    depth?: number;
    /** Evaluation domain of the source (node or edge source); drives Key and Work item attribute options in operands. */
    evaluationDomain?: EvaluationDomain;
    /** Declared derived attribute names from the graph; drives Derived attribute dropdown in operands. */
    declaredDerivedAttributeNames?: string[];
}

const DEFAULT_WHEN_TRUE: WhenDto = { true: true };

export const WhenBuilder: React.FC<WhenBuilderProps> = ({
    value,
    onChange,
    noRemove = false,
    depth = 0,
    evaluationDomain,
    declaredDerivedAttributeNames = [],
}) => {
    const ruleType = getWhenRuleType(value);
    const canRemove = !noRemove && depth > 0;

    const setRuleType = (type: string) => {
        if (type === "true") {
            onChange({ true: true });
            return;
        }
        if (type === "all") {
            onChange({ all: { conditions: [DEFAULT_WHEN_TRUE] } });
            return;
        }
        if (type === "any") {
            onChange({ any: { conditions: [DEFAULT_WHEN_TRUE] } });
            return;
        }
        if (type === "not") {
            onChange({ not: { condition: DEFAULT_WHEN_TRUE } });
            return;
        }
        const leftKey = defaultKeyOperandForEvaluationDomain(evaluationDomain);
        if (type === "eq" || type === "ne" || type === "gt" || type === "gte" || type === "lt" || type === "lte") {
            onChange({
                [type]: { left: leftKey, right: { value: "" } },
            } as WhenDto);
            return;
        }
        if (type === "in") {
            onChange({ in: { left: leftKey, right: [] } });
            return;
        }
        if (type === "startsWith") {
            onChange({ startsWith: { left: leftKey, prefix: "" } });
            return;
        }
        if (type === "endsWith") {
            onChange({ endsWith: { left: leftKey, postfix: "" } });
            return;
        }
        if (type === "contains") {
            onChange({ contains: { left: leftKey, substr: "" } });
            return;
        }
        if (type === "anyMatch") {
            onChange({ anyMatch: { over: "codes", when: DEFAULT_WHEN_TRUE } });
            return;
        }
        if (type === "noneMatch") {
            onChange({ noneMatch: { over: "codes", when: DEFAULT_WHEN_TRUE } });
            return;
        }
        if (type === "allMatch") {
            onChange({ allMatch: { over: "codes", when: DEFAULT_WHEN_TRUE } });
            return;
        }
        onChange({ true: true });
    };

    const commonRuleProps = {
        value,
        onChange,
        depth,
        evaluationDomain,
        declaredDerivedAttributeNames: declaredDerivedAttributeNames ?? [],
    };

    if (value == null || (ruleType === "true" && value.true !== true)) {
        return (
            <Stack direction="row" alignItems="center" spacing={1}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="when-rule-type-label">Rule type</InputLabel>
                    <Select
                        labelId="when-rule-type-label"
                        label="Rule type"
                        value="true"
                        onChange={(e) => setRuleType(e.target.value)}
                    >
                        {RULE_TYPE_GROUPS.flatMap((g) => [
                            ...(g.group != null ? [<ListSubheader key={g.group}>{g.group}</ListSubheader>] : []),
                            ...g.options.map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                    {t.label}
                                </MenuItem>
                            )),
                        ])}
                    </Select>
                </FormControl>
            </Stack>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.25, ml: depth * 2 }}>
            <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="when-rule-type-label">Rule type</InputLabel>
                        <Select
                            labelId="when-rule-type-label"
                            label="Rule type"
                            value={ruleType}
                            onChange={(e) => setRuleType(e.target.value)}
                        >
                            {RULE_TYPE_GROUPS.flatMap((g) => [
                                ...(g.group != null ? [<ListSubheader key={g.group}>{g.group}</ListSubheader>] : []),
                                ...g.options.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </FormControl>
                    {canRemove && (
                        <IconButton size="small" aria-label="Remove rule" onClick={() => onChange(null)} color="error">
                            <Delete fontSize="small" />
                        </IconButton>
                    )}
                </Stack>

                {(ruleType === "all" || ruleType === "any") && (
                    ruleType === "all" ? <RuleAll {...commonRuleProps} /> : <RuleAny {...commonRuleProps} />
                )}

                {ruleType === "not" && <RuleNot {...commonRuleProps} />}

                {(ruleType === "eq" || ruleType === "ne" || ruleType === "gt" || ruleType === "gte" || ruleType === "lt" || ruleType === "lte") && (
                    <RuleComparator {...commonRuleProps} ruleType={ruleType} />
                )}

                {ruleType === "in" && <RuleIn {...commonRuleProps} />}

                {ruleType === "startsWith" && <RuleStartsWith {...commonRuleProps} />}

                {ruleType === "endsWith" && <RuleEndsWith {...commonRuleProps} />}

                {ruleType === "contains" && <RuleContains {...commonRuleProps} />}

                {(ruleType === "anyMatch" || ruleType === "noneMatch" || ruleType === "allMatch") && (
                    <RuleQuantifier {...commonRuleProps} kind={ruleType as QuantifierKind} />
                )}
            </Stack>
        </Paper>
    );
};
